import Button from '@atlaskit/button/new';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { DatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import DatabaseResizeObserver from '@databases/layout/observer/ResizeObserver';
import SidePanel from '@databases/layout/SidePanel';
import { logError } from '@databases/logs';
import { useFullscreen, useShowTitle } from '@databases/native-embed-editor-plugin/state';
import usePanelState from '@databases/panel/usePanelState';
import { useCallbackEffect } from '@databases/react';
import { AsyncOperationStatus } from '@databases/redux-types';
import { appStoreUtils } from '@databases/redux/store';
import { DatabaseFieldType } from '@databases/types/database';
import { shouldShowField } from '@databases/types/database/view';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFields,
  getDatabaseValue,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus
} from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import React, {
  lazy,
  type FunctionComponent,
  Suspense,
  useCallback,
  useEffect
} from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';

import genericErrorIllustration from '../../../../assets/generic_error.svg';
import { Banners } from '../../../../common/components/database-content/Banners.tsx';
import DatabaseEntry, {
  type DatabaseEntryPresentation
} from '../../../../common/components/database-entry/DatabaseEntry.tsx';
import DatabaseEntrySkeleton from '../../../../common/components/database-entry/DatabaseEntrySkeleton.tsx';
import DatabaseEntryTitle from '../../../../common/components/database-entry/DatabaseEntryTitle.tsx';

const ShrinkingEmptyState = lazy(() =>
  dynamicImport(() => import('../../../../common/components/ShrinkingEmptyState.tsx'))
);

const EmbedEmptyState = lazy(() => dynamicImport(() => import('./EmbedEmptyState.tsx')));

const NativeEmbedEditorPluginReferenceToolbar = lazy(() =>
  dynamicImport(
    () =>
      import('../../../../common/components/database-content/toolbars/NativeEmbedEditorPluginReferenceToolbar.tsx')
  )
);

/**
 * Entry details view for the native embed editor plugin in referencing mode.
 * Similar to EmbedEntryDetails but replaces the PageHeader with
 * NativeEmbedEditorPluginReferenceToolbar.
 *
 * Uses SidePanel (overlay mode) and Banners from the existing infrastructure
 * to provide ViewSettingsPanel and UnsavedViewChangesBanner support without
 * duplicating that logic here.
 */
// overflowY:auto enables scrolling when content exceeds the max iframe height cap.
// height:100% is intentionally omitted - the iframe is dynamically resized to fit content.
const scrollContainerStyles = xcss({ overflowX: 'hidden', overflowY: 'auto' });
const v2TopPaddingStyles = xcss({ paddingBlockStart: 'space.200' });

const entryTitleStyles = xcss({
  font: 'font.body.large',
  fontWeight: 'font.weight.bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

const entryTitleRowStyles = xcss({
  paddingInlineStart: 'space.100',
  paddingBlockEnd: 'space.100'
});

const entryTitleDividerStyles = xcss({
  height: '1px'
});

const entryTitleDividerNoTitleStyles = xcss({
  paddingBlockStart: 'space.100'
});

const redesignedEntryStyles = xcss({
  paddingBlock: 'space.300',
  paddingInline: 'space.300'
});

const NativeEmbedEditorPluginReferenceEntryDetails: FunctionComponent<{
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  onDataLoaded?: () => void;
}> = ({ contentId, entryId, isContentEditable = true, onDataLoaded }) => {
  const { view } = useView();
  const fieldVisibilityMap = view.settings.fields;

  const [databaseExists, entryExists, fieldsLength, entryName, entryNameField] =
    appStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdOptional(state, { contentId });
      const entry =
        database &&
        !isBrokenDatabase(database) &&
        getDatabaseEntryOptional(database, entryId);
      const fields =
        database && !isBrokenDatabase(database) ? getDatabaseFields(database) : [];
      const firstVisibleField = fields.find((field) =>
        shouldShowField(fieldVisibilityMap, field.id)
      );
      let name: string | undefined;
      if (database && entry && !isBrokenDatabase(database) && firstVisibleField) {
        const representation = getStringRepresentation(
          {
            database,
            field: firstVisibleField,
            entry,
            value: getDatabaseValue(database, firstVisibleField, entry)
          },
          state
        );
        name =
          firstVisibleField.type === DatabaseFieldType.ENTRY_LINK &&
          representation?.startsWith('Unnamed entry:')
            ? ''
            : representation;
      }
      return [!!database, !!entry, fields.length, name, firstVisibleField] as const;
    });

  const isNativeEmbedsV2 =
    getExperimentValue('mauiExperiment', 'isEnabled', {
      fireExperimentExposure: false
    }) &&
    getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
      fireExperimentExposure: true
    });

  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = appStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(loadDatabaseByContentId(contentId));
    }, [contentId, dispatch])
  );
  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  const client = useAnalytics();
  const locale = useLocale();

  useCallbackEffect(
    useCallback(async () => {
      await client.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: DatabaseContainerType.EMBED_ENTRY,
        locale
      });
    }, [client, contentId, locale])
  );

  const { isPanelVisible } = usePanelState();
  const { isFullscreen } = useFullscreen();
  const showTitle = useShowTitle();

  useEffect(() => {
    if (databaseStatus?.status !== AsyncOperationStatus.SUCCEEDED) {
      return;
    }

    GlobalPageLoadExperience.mark('database-load-end');
    GlobalPageLoadExperience.success({ force: true });
    onDataLoaded?.();
  }, [contentId, databaseStatus?.status, onDataLoaded]);

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true
      });
      logError(new Error(`Couldn't load link with error: ${error}`));
      return (
        <Suspense fallback={<Spinner interactionName="load" />}>
          <ShrinkingEmptyState
            header={formatMessage(messages['embed-view.error-heading-text'])}
            description={formatMessage(messages['embed-view.error-description-text'])}
            imageUrl={genericErrorIllustration}
            primaryAction={
              <Button appearance="primary" onClick={() => globalThis.location.reload()}>
                {formatMessage(messages['embed-view.try-again-button-text'])}
              </Button>
            }
            maxImageWidth={300}
            maxImageHeight={250}
          />
        </Suspense>
      );
    }
    case AsyncOperationStatus.SUCCEEDED:
      break;
    default:
      return <DatabaseEntrySkeleton />;
  }

  if (!databaseExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  if (!entryExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  const isEntryDetailsRedesign = getExperimentValue(
    'databasesFrontendEntryDetailsRedesign',
    'isEnabled',
    { fireExperimentExposure: true }
  );

  const presentation: DatabaseEntryPresentation = isEntryDetailsRedesign
    ? { kind: 'entry-details-redesign' }
    : isNativeEmbedsV2
      ? { kind: 'native-embed-v2', hideFirstVisibleField: true }
      : { kind: 'default' };

  const entryContent = (
    <DatabaseEntry
      contentId={contentId}
      entryId={entryId}
      isContentEditable={isContentEditable}
      isEmbedEntryView
      presentation={presentation}
      fieldNameWidth={!isEntryDetailsRedesign && isNativeEmbedsV2 ? 200 : undefined}
      emptyValueLabel={
        isNativeEmbedsV2
          ? formatMessage(messages['embed-view.empty-entry-label'])
          : undefined
      }
    />
  );

  return (
    <>
      <Banners />

      <Box
        id="layout-toolbar-wrapper"
        xcss={isNativeEmbedsV2 && !isFullscreen && v2TopPaddingStyles}
      >
        <Suspense fallback={null}>
          <NativeEmbedEditorPluginReferenceToolbar
            contentId={contentId}
            entryName={isNativeEmbedsV2 ? entryName : undefined}
            unnamedEntryLabel={
              isNativeEmbedsV2
                ? formatMessage(messages['unnamed-entities.unnamed-entry'])
                : undefined
            }
            fieldVisibilityMap={fieldVisibilityMap}
            fieldsLength={fieldsLength}
            isNativeEmbedsV2={isNativeEmbedsV2}
          />
        </Suspense>
      </Box>

      {!isEntryDetailsRedesign && isNativeEmbedsV2 && showTitle && (
        <>
          <Box id="layout-entry-title-wrapper" xcss={entryTitleRowStyles}>
            <Box xcss={entryTitleStyles}>
              {entryName && entryName.trim().length > 0
                ? entryName
                : formatMessage(messages['unnamed-entities.unnamed-entry'])}
            </Box>
          </Box>
          <Box
            xcss={entryTitleDividerStyles}
            style={{ backgroundColor: token('color.border') }}
          />
        </>
      )}
      {!isEntryDetailsRedesign && isNativeEmbedsV2 && !showTitle && (
        <Box xcss={entryTitleDividerNoTitleStyles}>
          <Box
            xcss={entryTitleDividerStyles}
            style={{ backgroundColor: token('color.border') }}
          />
        </Box>
      )}

      <Box xcss={scrollContainerStyles}>
        <DatabaseResizeObserver>
          {isEntryDetailsRedesign ? (
            <Stack space="space.300" xcss={redesignedEntryStyles}>
              {showTitle && (
                <DatabaseEntryTitle
                  title={entryName}
                  titleField={entryNameField}
                  headingLevel="h1"
                />
              )}
              {entryContent}
            </Stack>
          ) : (
            entryContent
          )}
        </DatabaseResizeObserver>
      </Box>

      <SidePanel
        overlayMode
        contentId={contentId}
        isSidePanelVisible={isPanelVisible()}
      />
    </>
  );
};

export default NativeEmbedEditorPluginReferenceEntryDetails;
